/**
 * Bag Compatibility Calculator
 * 
 * Checks if user-entered dimensions are compatible with Celestron bag specifications.
 * Provides real-time feedback with accessibility support.
 */

class BagCompatibilityCalculator {
  constructor() {
    this.compatChecks = document.querySelectorAll('[data-compat-check]');
    this.init();
  }

  /**
   * Initialize the calculator by setting up event listeners
   */
  init() {
    if (!this.compatChecks || this.compatChecks.length === 0) {
      return;
    }

    this.compatChecks.forEach(fieldset => {
      const inputs = fieldset.querySelectorAll('input[type="number"]');
      
      inputs.forEach(input => {
        // Listen for input changes
        input.addEventListener('input', () => this.handleInputChange(fieldset));
        // Also handle change event for when user uses arrow keys or leaves field
        input.addEventListener('change', () => this.handleInputChange(fieldset));
      });
    });
  }

  /**
   * Handle input change and check compatibility
   * @param {HTMLElement} fieldset - The fieldset containing the inputs
   */
  handleInputChange(fieldset) {
    const inputs = fieldset.querySelectorAll('input[type="number"]');
    const resultElement = fieldset.querySelector('[data-result]');
    
    if (!resultElement) {
      return;
    }

    let isCompatible = true;
    let hasAllValues = true;

    // Check each input
    inputs.forEach(input => {
      const value = parseFloat(input.value);
      const maxDim = parseFloat(input.getAttribute('data-max-dim'));

      // Check if input has a value
      if (!input.value || input.value.trim() === '') {
        hasAllValues = false;
        return;
      }

      // Validate the value is a valid number
      if (isNaN(value) || value < 0) {
        hasAllValues = false;
        return;
      }

      // Check if value exceeds maximum dimension
      if (value > maxDim) {
        isCompatible = false;
      }
    });

    // Update the result based on the compatibility check
    this.updateResult(resultElement, hasAllValues, isCompatible);
  }

  /**
   * Update the result element with compatibility status
   * @param {HTMLElement} resultElement - The result div to update
   * @param {boolean} hasAllValues - Whether all inputs have values
   * @param {boolean} isCompatible - Whether dimensions are compatible
   */
  updateResult(resultElement, hasAllValues, isCompatible) {
    // Reset classes
    resultElement.classList.remove(
      'form-section__result--pending',
      'form-section__result--success',
      'form-section__result--error'
    );

    if (!hasAllValues) {
      // Not all dimensions entered yet
      resultElement.classList.add('form-section__result--pending');
      resultElement.textContent = 'Enter dimensions above to check for compatibility';
    } else if (isCompatible) {
      // Compatible - all dimensions fit
      resultElement.classList.add('form-section__result--success');
      resultElement.textContent = '✓ Compatible - Your equipment fits in this bag';
    } else {
      // Not compatible - at least one dimension exceeds maximum
      resultElement.classList.add('form-section__result--error');
      resultElement.textContent = '✗ Not Compatible - One or more dimensions exceed the bag\'s capacity';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BagCompatibilityCalculator();
  });
} else {
  // DOM is already loaded
  new BagCompatibilityCalculator();
}
